<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="AdminIndex" label="Admin" />
      <breadcrumb-link view="AdminReimbursementList" label="Reimbursements" />
      <breadcrumb-link view="AdminReimbursementUpload" label="Upload" :active="true" />
    </breadcrumb-list>
    <section>
      <b-loading v-model="loading" :is-full-page="false"></b-loading>
      <div class="container is-max-hd">
        <div class="box">
          <div class="title-with-details mb-4">
            <h1 class="title">Upload Reimbursements</h1>
            <a @click="showUploadDetailModal = true" class="feature-details-link-title">
              <span class="details-text">See details</span>
              <b-icon icon="magnify-plus" size="is-small" class="bounce-icon"></b-icon>
            </a>
          </div>
          <hr />
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <b-field
                  class="file is-primary"
                  :message="errors.get('file')"
                  :type="{ 'is-danger': errors.has('file') }"
                >
                  <b-upload v-model="file" class="file-label" accept=".csv">
                    <span class="file-cta">
                      <b-icon class="file-icon" icon="upload"></b-icon>
                      <span class="file-label">Select CSV File</span>
                    </span>
                    <span class="file-name" v-if="file">
                      {{ file.name }} ({{ formatFileSize(file.size) }})
                    </span>
                  </b-upload>
                </b-field>
              </div>
            </div>
            <div class="level-right">
              <p class="level-item">
                <b-button :loading="loading" @click="handleSubmit()" type="is-primary">
                  Upload
                </b-button>
              </p>
            </div>
          </div>
        </div>
        <div class="has-text-centered">
          <b-button
            type="is-primary is-outlined"
            tag="router-link"
            :to="{ name: 'AdminReimbursementList' }"
          >
            Nevermind, take me back.
          </b-button>
        </div>
      </div>
    </section>

    <!-- Reimbursement Upload Detail Modal -->
    <b-modal v-model="showUploadDetailModal" :width="800" scroll="keep">
      <ReimbursementUploadDetail></ReimbursementUploadDetail>
    </b-modal>
  </div>
</template>

<script>
import request from '../../request';
import Errors from '../../utils/errors.js';
import ReimbursementUploadDetail from '../../components/ReimbursementUploadDetail.vue';

export default {
  name: 'AdminReimbursementUpload',
  components: {
    ReimbursementUploadDetail,
  },
  data() {
    return {
      errors: new Errors(),
      file: null,
      loading: false,
      showUploadDetailModal: false,
    };
  },
  methods: {
    toast(msg, msgType) {
      this.$buefy.toast.open({
        message: msg,
        type: msgType,
        duration: 5000,
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    handleSubmit() {
      // Validate file selection
      if (!this.file) {
        this.toast('Please select a CSV file to upload.', 'is-warning');
        return;
      }

      // Validate file type
      if (!this.file.name.toLowerCase().endsWith('.csv')) {
        this.toast('Please select a valid CSV file.', 'is-warning');
        return;
      }

      // Validate file size (max 100MB as per backend)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (this.file.size > maxSize) {
        this.toast('File size exceeds 100MB limit. Please select a smaller file.', 'is-warning');
        return;
      }

      this.$buefy.dialog.confirm({
        message: 'NOTE: This action is irreversible. Do you want to continue?',
        onConfirm: () => {
          this.loading = true;
          let formData = new FormData();
          formData.append('file', this.file);
          request({
            url: '/admin/reimbursements/upload',
            data: formData,
            method: 'POST',
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
            .then((response) => {
              const count = response.data.count || 0;
              this.toast(`Successfully imported ${count} reimbursements.`, 'is-success');
              this.$router.push({ name: 'AdminReimbursementList' });
            })
            .catch((err) => {
              console.error('Upload error:', err);
              if (err.response && err.response.status === 422) {
                this.errors.record(err.response.data.errors);
              } else if (err.response && err.response.data && err.response.data.error) {
                this.toast(`Upload failed: ${err.response.data.error}`, 'is-danger');
              } else {
                this.toast(
                  'Reimbursements failed to upload. Please check the file format and try again.',
                  'is-danger'
                );
              }
            })
            .finally(() => {
              this.loading = false;
            });
        },
      });
    },
  },
};
</script>

<style scoped>
.title-with-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.feature-details-link-title {
  color: #7c3aed;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  text-decoration: none;
}

.feature-details-link-title:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.details-text {
  font-weight: 500;
}

.bounce-icon {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}
</style>
